import React, {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { GreenroomState as GS } from '../../../stores/types/types';
import useLoad from '../../../hooks/use-load';
import WaitingIndicator from '../../general-components/waiting-indicator';
import OnsiteLocationEntry from './onsite-location-entry';
import {
	OnsiteLocationMainWrapper,
	OnsiteLocationMainBody,
	OnsiteLocationMainFooter,
	GradientPurple,
	GradientYellow,
	BackButton,
	ButtonLabel,
} from './elements';
import OnsiteLocationPublishing from './onsite-location-publishing';
import OnsiteLocationCanvas from './onsite-location-canvas';
import { useMediaDevicesContext } from '../../../contexts/media/check-media';
import { setModalState } from '../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../stores/reducers/modal';
import { SocketContext } from '../../../contexts/socket';
import {
	LOCATION_CANVAS_ROUTES,
	LOCATION_PUBLISHING_ROUTES,
	OnsiteLocationSession,
	OnsiteLocationSessionRoute,
} from '../../../stores/reducers/video';
import FeedMediaControls from '../../main-video/feed-media-controls';

declare global {
	interface Window {
		onsitePublisherStream?: {
			onsiteLocationId: number;
			srcObject: MediaStream;
		}
	}
}

interface Props {
	onsiteLocationSession: OnsiteLocationSession;
}

export default function OnsiteLocationMain({ onsiteLocationSession }: Props) {
	const dispatch = useDispatch();

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const onsiteLocations = useSelector((s: GS) => s.VideoReducer.onsiteLocations);
	const onsiteLocationsLoaded = useSelector((s: GS) => s.VideoReducer.onsiteLocationsLoaded);
	const mainVideoStream = useSelector((s: GS) => s.VideoReducer.mainVideoStream);

	const {
		id: onsiteLocationId,
		route: onsiteLocationRoute,
	} = onsiteLocationSession;

	const [hasLoaded, setHasLoaded] = useState<boolean>(false);
	const [promptForPermissions, setPromptForPermissions] = useState<boolean>(true);
	const [devicesChecked, setDevicesChecked] = useState<boolean>(false);

	const popupRef = useRef<Window | null>(null);

	const { checkMediaDevices } = useMediaDevicesContext();
	const { loadMeetingRoom } = useLoad();
	const { socket } = useContext(SocketContext);

	const selectedOnsiteLocation = useMemo(
		() => onsiteLocations.find((location) => location.id === onsiteLocationId),
		[onsiteLocations, onsiteLocationId],
	);

	const handleBack = () => {
		if (!meeting) return;
		document.location.href = `${window.location.origin}/producer/${meeting.meeting_id}`;
	};

	const handleOpenCanvas = () => {
		const url = new URL(window.location.href);
		url.searchParams.set('view', 'canvas');
		// open canvas as fullscreen popup
		popupRef.current = window.open(url, '_blank', `width=${window.screen.width},height=${window.screen.height}`);
	};

	useEffect(() => {
		// if the selectOnsiteLocation has been deleted, the producer should return to the meeting
		if (
			onsiteLocationsLoaded
			&& (!selectedOnsiteLocation && onsiteLocationId)
		) {
			handleBack();
		}
	}, [onsiteLocationsLoaded, selectedOnsiteLocation, onsiteLocationId]);

	useEffect(() => {
		if (!hasLoaded) {
			if (token && meeting) {
				loadMeetingRoom({
					passedInToken: token,
					passedInMeeting: meeting,
					embed: false,
					isReload: false,
					successCallback: () => {
						setHasLoaded(true);
					},
				});
			}
		}
	}, [token, meeting, hasLoaded]);

	useEffect(() => {
		navigator.permissions.query({ name: 'camera' as PermissionName }).then((perm) => {
			if (perm.state === 'granted') {
				setPromptForPermissions(false);
			}
		});
	}, []);

	useEffect(() => {
		if (LOCATION_PUBLISHING_ROUTES.includes(onsiteLocationRoute)) {
			if (
				promptForPermissions
				&& onsiteLocationRoute !== OnsiteLocationSessionRoute.LimitedInputCanvas
			) {
				dispatch(setModalState({
					modalOpen: true,
					modalTemplate: MODAL_TEMPLATES.DEVICE_PERMISSION,
				}));
			}
			checkMediaDevices().then(() => {
				setDevicesChecked(true);
				dispatch(setModalState({
					modalOpen: false,
					modalTemplate: MODAL_TEMPLATES.DEVICE_PERMISSION,
				}));
			});
		}
	}, [onsiteLocationRoute]);

	useEffect(() => {
		// use the local publisher video
		if (onsiteLocationId && mainVideoStream instanceof MediaStream) {
			const stream = {
				onsiteLocationId,
				srcObject: mainVideoStream,
			};
			if (popupRef.current) {
				popupRef.current.onsitePublisherStream = stream; // popup canvas
			}
			if (onsiteLocationRoute === OnsiteLocationSessionRoute.LimitedInputCanvas) {
				window.onsitePublisherStream = stream; // limited input canvas
			}
		}
	}, [popupRef.current, mainVideoStream, onsiteLocationRoute]);

	return (
		<>
			{!selectedOnsiteLocation && onsiteLocationId && (
				<div className="wrapper animate">
					<WaitingIndicator />
				</div>
			)}
			{!selectedOnsiteLocation && !onsiteLocationId && (
				// limited input sessions can use the canvas view without a selected location
				<OnsiteLocationCanvas socket={socket} devicesChecked={devicesChecked} />
			)}
			{selectedOnsiteLocation && (
				<OnsiteLocationMainWrapper>
					{LOCATION_CANVAS_ROUTES.includes(onsiteLocationRoute) ? (
						<OnsiteLocationCanvas socket={socket} devicesChecked={devicesChecked} />
					) : (
						<>
							<GradientPurple className={classNames(onsiteLocationRoute === OnsiteLocationSessionRoute.Publishing && 'left')} />
							<GradientYellow className={classNames(onsiteLocationRoute === OnsiteLocationSessionRoute.Publishing && 'left')} />
							<OnsiteLocationMainBody>
								{onsiteLocationRoute === OnsiteLocationSessionRoute.Publishing ? (
									<OnsiteLocationPublishing
										onsiteLocation={selectedOnsiteLocation}
										devicesChecked={devicesChecked}
										handleOpenCanvas={handleOpenCanvas}
									/>
								) : (
									<OnsiteLocationEntry
										onsiteLocation={selectedOnsiteLocation}
										socket={socket}
										handleOpenCanvas={handleOpenCanvas}
									/>
								)}
							</OnsiteLocationMainBody>
							<OnsiteLocationMainFooter>
								{onsiteLocationRoute === OnsiteLocationSessionRoute.Entry && (
									<BackButton onClick={handleBack}>
										<Icon name={ICONS.ARROW_LEFT} size={12} color={COLORS.WHITE} />
										<ButtonLabel className="grayscale-200">Back</ButtonLabel>
									</BackButton>
								)}
								<img src='/brandlive_logo.png' alt="" />
								<FeedMediaControls></FeedMediaControls>
							</OnsiteLocationMainFooter>
						</>
					)}
				</OnsiteLocationMainWrapper>
			)}
		</>
	);
}
